import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Grid, Chip, LinearProgress } from '@mui/material';
import { format, isSameDay } from 'date-fns';

function DailyCheckinModal({ open, onClose, selectedDate, checkins }) {
  const dayCheckins = checkins.filter(checkin => 
    isSameDay(new Date(checkin.timestamp), selectedDate)
  );

  const getMoodEmoji = (mood) => {
    const emojis = {
      'Excellent': '😄', 'Good': '🙂', 'Okay': '😐', 'Poor': '😔', 'Terrible': '😢'
    };
    return emojis[mood] || '😐';
  };

  const getProgressColor = (value, max = 10) => {
    const percentage = (value / max) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'primary';
    if (percentage >= 40) return 'warning';
    return 'error';
  };

  if (dayCheckins.length === 0) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{format(selectedDate, 'EEEE, MMMM d, yyyy')}</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">No check-in recorded for this day</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Complete a check-in to track your daily progress!
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
      </Dialog>
    );
  }

  const checkin = dayCheckins[0];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</Typography>
          <Chip label={`${getMoodEmoji(checkin.mood)} ${checkin.mood}`} color="primary" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Check-in at {format(new Date(checkin.timestamp), 'h:mm a')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>Core Wellness</Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Energy Level</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{checkin.energy}/10</Typography>
              </Box>
              <LinearProgress variant="determinate" value={(checkin.energy / 10) * 100} 
                color={getProgressColor(checkin.energy)} sx={{ height: 8, borderRadius: 4 }} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Soreness Level</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{checkin.soreness}/10</Typography>
              </Box>
              <LinearProgress variant="determinate" value={(checkin.soreness / 10) * 100} 
                color={getProgressColor(10 - checkin.soreness)} sx={{ height: 8, borderRadius: 4 }} />
            </Box>

            {checkin.weight && (
              <Typography variant="body2">Weight: <strong>{checkin.weight} lbs</strong></Typography>
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>Sleep & Recovery</Typography>
            <Typography variant="body2">Sleep: <strong>{checkin.sleepHours || 'N/A'} hours</strong></Typography>
            <Typography variant="body2">Quality: <strong>{checkin.sleepQuality || 'N/A'}</strong></Typography>
            <Typography variant="body2">Recovery: <strong>{checkin.recovery || 'N/A'}</strong></Typography>
          </Grid>

          {checkin.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>Notes</Typography>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, borderLeft: 4, borderColor: 'primary.main' }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>"{checkin.notes}"</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions><Button onClick={onClose} variant="contained">Close</Button></DialogActions>
    </Dialog>
  );
}

export default DailyCheckinModal;

